import { AxiosPromise } from 'axios';
import { http } from 'services';
import type { IEntity } from '../auth/types';
import type { OnlinePaymentMethod, PaymentMethod } from '../payments/methods';

// Fetch entitlements for the active org.
// The frontend typically reads entitlements from the auth context (set at profile fetch),
// but this endpoint is available for explicit refreshes — e.g. after a plan upgrade.
export const GetEntitlements = (signal?: AbortSignal): AxiosPromise<IEntity.OrgEntitlements> =>
  http.request.get('/api/entitlements', { signal });

// Fetch live resource counts (assessments, members, documents) for the billing page.
export const GetResourceCounts = (): AxiosPromise<{ assessments: number; members: number; documents: number }> =>
  http.request.get('/api/entitlements/resource-counts');

// Fetch the public plan catalogue for pricing page / upgrade modal.
export const GetPlans = (): AxiosPromise<Plan[]> =>
  http.request.get('/api/entitlements/plans');

export interface Plan {
  key: string;
  displayName: string;
  description?: string;
  monthlyPrice: number;
  isPublic: boolean;
  sortOrder: number;
  allowedModels: string[];
  trialDays: number;
  features: Array<{
    featureKey: string;
    enabled: boolean;
    limit?: number | null;
  }>;
}

export interface TokenUsageEntry {
  userId: string;
  userName: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  lastUsedAt: string;
}

export interface TokenUsageBreakdown {
  periodStart: string;
  orgTotal: { used: number; limit: number | null };
  byUser: TokenUsageEntry[];
}

// ─── Payments (Payme / Click / ATMOS) ───────────────────────────────────────────────

// The provider that settles an order; Uzcard and Humo are both paid on the ATMOS card page.
export type PaymentProvider = 'payme' | 'click' | 'atmos';
// Only monthly billing is offered.
export type BillingCycle = 'monthly';

// Create a checkout session. When `status === 'pending'` redirect the browser to
// `checkoutUrl`; the subscription is activated by the provider's server-to-server
// callback, not by the browser returning. When `status === 'activated'` coins
// fully covered the invoice and the plan is already active — `checkoutUrl` points
// to the billing-return page which confirms the new plan.
export const CreateCheckout = (dto: {
  planKey: string;
  billingCycle: BillingCycle;
  method: OnlinePaymentMethod;
  applyCoins?: boolean;
  promocode?: string;
  referralCode?: string;
}): AxiosPromise<{
  provider: PaymentProvider;
  checkoutUrl: string;
  orderId: string;
  status: 'pending' | 'activated';
}> => http.request.post('/api/payments/checkout', dto);

// Buy wallet coins. The charged so'm amount is re-derived server-side as
// `coins × 1`; redirect the browser to `checkoutUrl`. The wallet is credited by
// the provider's server-to-server callback, not by the browser returning.
export const CreateCoinTopup = (dto: {
  method: OnlinePaymentMethod;
  coins: number;
}): AxiosPromise<{
  provider: PaymentProvider;
  checkoutUrl: string;
  orderId: string;
  // A top-up always pays a provider, so this is always 'pending'.
  status: 'pending' | 'activated';
}> => http.request.post('/api/payments/coins/checkout', dto);

// The payment methods a customer can pay with right now: switched on by a platform
// admin and configured on the server, in display order. Plans may also offer the
// manual card transfer; coin top-ups only offer online methods.
export const GetPaymentMethods = (): AxiosPromise<{ plan: PaymentMethod[]; coins: OnlinePaymentMethod[] }> =>
  http.request.get('/api/payments/methods');

// Ask the server to re-check an order with the provider. Only ATMOS orders are
// re-checked; it lets the return page activate a paid order whose server-to-server
// callback has not arrived. Every other order just reports its state.
export const SyncPaymentOrder = (
  orderId: string,
): AxiosPromise<{ state: 'pending' | 'created' | 'paid' | 'cancelled' | 'refunded' }> =>
  http.request.post(`/api/payments/orders/${encodeURIComponent(orderId)}/sync`);

// Request an upgrade for a custom-priced (Enterprise) plan — emails sales.
export const RequestUpgrade = (dto: {
  message?: string;
  requestedPlan?: string;
  contactEmail?: string;
  contactPhone?: string;
}): AxiosPromise<{ ok: true }> => http.request.post('/api/payments/request-upgrade', dto);

// ─── Admin endpoints ──────────────────────────────────────────────────────────

export interface AdminSubscription {
  subscriptionId: string;
  org: { id: string; name: string; slug: string; tier: string };
  plan: { key: string; displayName: string };
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId?: string;
  createdAt?: string;
}

export const AdminListSubscriptions = (): AxiosPromise<AdminSubscription[]> =>
  http.request.get('/api/entitlements/admin/subscriptions');

export const AdminGetAllPlans = (): AxiosPromise<Plan[]> =>
  http.request.get('/api/entitlements/admin/plans');

export const AdminAssignPlan = (orgId: string, planKey: string, status: 'active' | 'trialing' = 'active') =>
  http.request.post(`/api/entitlements/admin/org/${orgId}/assign-plan`, { planKey, status });

export const AdminRevokeSubscription = (orgId: string) =>
  http.request.delete(`/api/entitlements/admin/org/${orgId}/subscription`);

// Get per-user token usage breakdown for the active org (current month by default)
export const GetTokenUsage = (periodStart?: string): AxiosPromise<TokenUsageBreakdown> =>
  http.request.get('/api/entitlements/token-usage', { params: periodStart ? { periodStart } : {} });

// Admin: update a plan's token limit, allowed models, and/or trial days
export const AdminUpdatePlan = (
  planKey: string,
  dto: { tokenLimit?: number | null; allowedModels?: string[]; trialDays?: number },
) => http.request.patch(`/api/entitlements/admin/plans/${planKey}`, dto);
